/**
 * Utility functions for Intake page
 */

import type {
  Potravina,
  NutritionalValues,
  Prijem,
  PrijemKategoria,
  IntakeByCategory,
  MealCategoryTotals,
} from "./types";

/**
 * Calculate nutritional values for a given food and amount.
 * Accepts 0 g correctly, falling back to package size or 100 only when
 * hmotnost is not a valid number (null, undefined, empty string, etc.).
 */
export function calculateNutrition(
  potravina: Potravina,
  hmotnost: string | null
): NutritionalValues {
  // Parse hmotnost only; use 0 if explicitly "0", fallback to balenieG or 100 only when invalid
  const hmotnostNum = parseFloat(hmotnost || "");
  const weight =
    !isNaN(hmotnostNum) && hmotnostNum >= 0
      ? hmotnostNum
      : parseFloat(potravina.balenieG || "100") || 100;

  const factor = weight / 100;

  // Prioritize energiaKcal if available, otherwise convert from energiaKj
  const energiaKcal = potravina.energiaKcal
    ? (parseFloat(potravina.energiaKcal) * factor).toFixed(1)
    : ((parseFloat(potravina.energiaKj || "0") * factor) / 4.18).toFixed(1);

  return {
    energiaKcal,
    proteinG: (parseFloat(potravina.proteinG || "0") * factor).toFixed(1),
    tukG: (parseFloat(potravina.tukG || "0") * factor).toFixed(1),
    sacharidG: (parseFloat(potravina.sacharidG || "0") * factor).toFixed(1),
    nmkG: (parseFloat(potravina.nmkG || "0") * factor).toFixed(1),
    vlakninaG: (parseFloat(potravina.vlakninaG || "0") * factor).toFixed(1),
    sodikG: (parseFloat(potravina.sodikG || "0") * factor).toFixed(2),
  };
}

/**
 * Map meal category names for display
 */
export function getMealCategoryDisplayName(name: string): string {
  const nameMap: Record<string, string> = {
    ranajky: "raňajky",
    vecera: "večera",
    obed: "obed",
  };
  return nameMap[name.toLowerCase()] || name;
}

/**
 * Group intake records by meal category
 */
export function groupIntakeByCategory(
  prijem: Prijem[] | undefined,
  prijemKategorie: PrijemKategoria[] | undefined
): IntakeByCategory {
  const grouped: IntakeByCategory = {};

  // Initialize all meal categories
  prijemKategorie?.forEach(kategoria => {
    grouped[kategoria.id] = [];
  });

  // Group items
  prijem?.forEach(p => {
    const kategoriaId = p.prijemKategoriaId;
    if (kategoriaId && grouped[kategoriaId]) {
      grouped[kategoriaId].push(p);
    }
  });

  return grouped;
}

/**
 * Calculate totals for a specific meal category
 */
export function getMealCategoryTotals(items: Prijem[]): MealCategoryTotals {
  let totalEnergiaKcal = 0;
  let totalProteinG = 0;
  let totalTukG = 0;
  let totalSacharidG = 0;

  items.forEach(item => {
    // Skip inactive alternative rows — they do not count toward totals
    if (item.aktivne === false) return;
    const nutrition = calculateNutrition(item.potravina, item.hmotnost);
    totalEnergiaKcal += parseFloat(nutrition.energiaKcal) || 0;
    totalProteinG += parseFloat(nutrition.proteinG) || 0;
    totalTukG += parseFloat(nutrition.tukG) || 0;
    totalSacharidG += parseFloat(nutrition.sacharidG) || 0;
  });

  return {
    totalEnergiaKcal: totalEnergiaKcal.toFixed(1),
    totalProteinG: totalProteinG.toFixed(1),
    totalTukG: totalTukG.toFixed(1),
    totalSacharidG: totalSacharidG.toFixed(1),
  };
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("sk-SK", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
